/**
 * Typed fetch client.
 *
 * Responsibilities kept here so no screen re-implements them:
 *  - attach the bearer token to every request, including the image routes (previews and originals
 *    are patient data and are role-checked server side, so they cannot be plain <img src>);
 *  - turn a 401 into a single, global "session expired" event rather than a per-screen error;
 *  - surface the backend's own error message when it sends one, because upload rejections carry
 *    the human-readable reason a clerk needs ("password protected", "412 pages, limit is 500").
 */

import type {
  CasePatch,
  Batch,
  Capability,
  CapabilitiesResponse,
  Case,
  Checklist,
  ChecklistInput,
  CompletenessResponse,
  DashboardResponse,
  DiagnosisDetail,
  DiagnosisExtraction,
  DiagnosisReviewAction,
  DocumentSummary,
  IntakeFormValues,
  IntakeLookupResponse,
  IntakeOptions,
  IntakeResponse,
  Job,
  LoginResponse,
  Paged,
  PageDetail,
  PageReviewAction,
  PageReplaceResult,
  PageReviewResult,
  PageSummary,
  RetentionInfo,
  PrescriptionAnalysisResponse,
  PrescriptionAnalysisSummary,
  Qualifier,
  ThresholdsResponse,
  UploadResultRow,
  User,
} from './types';

export const API_BASE = '/api';
const TOKEN_KEY = 'opd.auth.token';
const USER_KEY = 'opd.auth.user';

/** Dispatched when the server rejects the token; App listens and routes to /login. */
export const UNAUTHORIZED_EVENT = 'opd:unauthorized';

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

// ------------------------------------------------------------- token store

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setSession(token: string, user: User): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch {
    /* private-mode browsers: the session simply does not survive a reload */
  }
}

export function getStoredUser(): User | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch {
    /* ignore */
  }
  imageMemory.clear();
  try {
    void caches?.delete(IMAGE_CACHE);
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------- core

function authHeaders(extra?: HeadersInit): Headers {
  const h = new Headers(extra);
  const token = getToken();
  if (token) h.set('Authorization', `Bearer ${token}`);
  return h;
}

async function readError(res: Response): Promise<{ message: string; body: unknown }> {
  const text = await res.text().catch(() => '');
  if (!text) return { message: `${res.status} ${res.statusText}`, body: null };
  try {
    const parsed = JSON.parse(text) as Record<string, unknown>;
    // FastAPI puts the message in `detail`; it may itself be a list of validation errors.
    const detail = parsed.detail ?? parsed.message ?? parsed.error;
    if (typeof detail === 'string') return { message: detail, body: parsed };
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0] as Record<string, unknown>;
      const msg = typeof first?.msg === 'string' ? first.msg : JSON.stringify(detail);
      return { message: msg, body: parsed };
    }
    return { message: `${res.status} ${res.statusText}`, body: parsed };
  } catch {
    return { message: text.slice(0, 300), body: text };
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers: authHeaders(init.headers) });

  if (res.status === 401) {
    clearSession();
    window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
    throw new ApiError(401, 'Your session has expired. Please sign in again.');
  }
  if (!res.ok) {
    const { message, body } = await readError(res);
    throw new ApiError(res.status, message, body);
  }
  if (res.status === 204) return undefined as T;

  const ct = res.headers.get('content-type') ?? '';
  if (!ct.includes('application/json')) return (await res.text()) as unknown as T;
  return (await res.json()) as T;
}

function qs(params: Record<string, unknown> | URLSearchParams | undefined): string {
  if (!params) return '';
  const sp = params instanceof URLSearchParams ? params : new URLSearchParams();
  if (!(params instanceof URLSearchParams)) {
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null || v === '') continue;
      if (Array.isArray(v)) v.forEach((item) => sp.append(k, String(item)));
      else sp.set(k, String(v));
    }
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

// ----------------------------------------------------- authenticated blobs

/**
 * Fetch an image route with the bearer token and return an object URL.
 *
 * The caller MUST revoke the URL when it is done (see `useAuthedObjectUrl`). Images cannot be
 * loaded with a bare `src` because every file route is role-checked and expects the header.
 */
/**
 * Page images are immutable per page version — a rescan creates a new version id — so thumbnails,
 * previews and renders are kept in memory for the session and in the browser's Cache Storage across
 * reloads. Without this every refresh re-downloaded every thumbnail and the preview. Annotated
 * images are not cached: their overlays change when a review or finding changes. The cache is
 * dropped on sign-out, since these are patient images.
 */
const IMAGE_CACHE = 'page-images-v1';
const imageMemory = new Map<string, Blob>();
const CACHEABLE = /^\/pages\/[^/]+\/(thumb|preview|image)$/;
// Patient images must not outlive a working day on a shared ward computer, even when nobody
// signed out: entries older than this are discarded and fetched again.
const IMAGE_CACHE_MAX_AGE_MS = 8 * 60 * 60 * 1000;

/** Drop the on-disk image cache when the browser starts with nobody signed in. */
export function purgeImageCacheIfSignedOut(): void {
  try {
    if (!localStorage.getItem(TOKEN_KEY)) void caches.delete(IMAGE_CACHE);
  } catch {
    /* ignore */
  }
}

/** A blob URL for an image already held in memory, so a remount can render without a flash. */
export function cachedObjectUrl(path: string): string | null {
  const blob = imageMemory.get(path);
  return blob ? URL.createObjectURL(blob) : null;
}

export async function fetchObjectUrl(path: string): Promise<string> {
  const cacheable = CACHEABLE.test(path);
  if (cacheable) {
    const inMemory = imageMemory.get(path);
    if (inMemory) return URL.createObjectURL(inMemory);
    try {
      const store = await caches.open(IMAGE_CACHE);
      let hit = await store.match(path);
      const cachedAt = Number(hit?.headers.get('X-Cached-At') ?? 0);
      if (hit && Date.now() - cachedAt > IMAGE_CACHE_MAX_AGE_MS) {
        await store.delete(path);
        hit = undefined;
      }
      if (hit) {
        const blob = await hit.blob();
        imageMemory.set(path, blob);
        return URL.createObjectURL(blob);
      }
    } catch {
      /* Cache Storage unavailable (private window, http origin): fall through to the network. */
    }
  }
  const res = await fetch(`${API_BASE}${path}`, { headers: authHeaders() });
  if (res.status === 401) {
    clearSession();
    window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
    throw new ApiError(401, 'Your session has expired. Please sign in again.');
  }
  if (!res.ok) {
    const { message } = await readError(res);
    throw new ApiError(res.status, message);
  }
  const blob = await res.blob();
  if (cacheable) {
    imageMemory.set(path, blob);
    try {
      await (await caches.open(IMAGE_CACHE)).put(
        path,
        new Response(blob, {
          headers: { 'Content-Type': blob.type, 'X-Cached-At': String(Date.now()) },
        }),
      );
    } catch {
      /* ignore — the memory copy still serves this session */
    }
  }
  return URL.createObjectURL(blob);
}

/**
 * Download an export. Same auth path as everything else, then a synthetic anchor click so the
 * browser saves it under the server-supplied filename where one is given.
 */
export async function downloadFile(path: string, fallbackName: string): Promise<void> {
  const res = await fetch(`${API_BASE}${path}`, { headers: authHeaders() });
  if (res.status === 401) {
    clearSession();
    window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
    throw new ApiError(401, 'Your session has expired. Please sign in again.');
  }
  if (!res.ok) {
    const { message } = await readError(res);
    throw new ApiError(res.status, message);
  }
  const disposition = res.headers.get('content-disposition') ?? '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const name = match ? decodeURIComponent(match[1]) : fallbackName;

  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Give the browser a tick to start the save before the blob disappears.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

// ------------------------------------------------------------------ auth

export const api = {
  /** `identifier` is an email address or a username — the server matches either. */
  async login(identifier: string, password: string): Promise<LoginResponse> {
    // The token endpoint is form-encoded (OAuth2 password flow), unlike the rest of the API.
    const body = new URLSearchParams({ username: identifier, password });
    const res = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    });
    if (!res.ok) {
      const { message, body: errBody } = await readError(res);
      throw new ApiError(
        res.status,
        res.status === 401 ? 'Email or password not recognised.' : message,
        errBody,
      );
    }
    return (await res.json()) as LoginResponse;
  },

  me: () => request<User>('/auth/me'),
  listUsers: () => request<User[]>('/auth/users'),
  createUser: (payload: {
    email?: string;
    username?: string;
    full_name: string;
    password: string;
    role: string;
  }) =>
    request<User>('/auth/users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  updateUser: (
    id: string,
    payload: {
      email?: string | null;
      username?: string | null;
      full_name?: string;
      role?: string;
      is_active?: boolean;
    },
  ) =>
    request<User>(`/auth/users/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),

  // --------------------------------------------------------- batches/cases

  listBatches: (params?: { q?: string; from?: string; to?: string }) =>
    request<Batch[]>(`/batches${qs(params)}`),
  createBatch: (payload: { name: string; note?: string }) =>
    request<Batch>('/batches', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  getBatch: (id: string) => request<Batch>(`/batches/${id}`),

  listCases: (params?: { batch_id?: string; patient_ref?: string; encounter_ref?: string }) =>
    request<Case[]>(`/cases${qs(params)}`),
  listCasesPaged: (params: {
    patient_ref?: string;
    encounter_ref?: string;
    created_from?: string;
    created_to?: string;
    limit: string;
    offset: string;
  }) => request<Paged<Case> & { grand_total: number }>(`/cases/paged${qs(params)}`),
  createCase: (payload: {
    batch_id: string;
    patient_ref: string;
    encounter_ref: string;
    checklist_id?: string | null;
  }) =>
    request<Case>('/cases', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  /** Records who confirmed the patient/encounter reference. References are never auto-merged. */
  confirmCase: (id: string) => request<Case>(`/cases/${id}/confirm`, { method: 'PATCH' }),

  getCase: (id: string) => request<Case>(`/cases/${id}`),

  /**
   * The original upload, exactly as it arrived.
   *
   * A path rather than a URL: the route is behind a bearer token, so it has to be fetched into an
   * object URL rather than dropped into an `href`.
   */
  documentFilePath: (documentId: string) => `/documents/${documentId}/file`,

  /** Correct a patient's details. Only the fields passed are written. */
  updateCase: (id: string, patch: CasePatch) =>
    request<Case>(`/cases/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    }),

  /** Admin only, and destructive: the case's documents, pages and analyses go with it. */
  deleteCase: (id: string) => request<void>(`/cases/${id}`, { method: 'DELETE' }),

  getCompleteness: (caseId: string) => request<CompletenessResponse>(`/cases/${caseId}/completeness`),
  recomputeCompleteness: (caseId: string) =>
    request<CompletenessResponse>(`/cases/${caseId}/completeness/recompute`, { method: 'POST' }),

  // ------------------------------------------------------------ documents

  listDocuments: (params: URLSearchParams) =>
    request<Paged<DocumentSummary>>(`/documents${qs(params)}`),
  getDocument: (id: string) => request<DocumentSummary & { pages?: PageSummary[] }>(`/documents/${id}`),
  deleteDocument: (id: string) => request<void>(`/documents/${id}`, { method: 'DELETE' }),

  /**
   * Upload one file at a time so the UI can show per-file progress and a per-file result.
   * XMLHttpRequest rather than fetch: fetch still has no upload progress event.
   */
  uploadDocument(
    file: File,
    fields: { batch_id: string; case_id?: string | null },
    onProgress?: (fraction: number) => void,
    signal?: AbortSignal,
  ): Promise<UploadResultRow> {
    return new Promise<UploadResultRow>((resolve, reject) => {
      const form = new FormData();
      form.append('files', file, file.name);
      form.append('batch_id', fields.batch_id);
      if (fields.case_id) form.append('case_id', fields.case_id);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_BASE}/documents/upload`);
      const token = getToken();
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
      };

      xhr.onload = () => {
        if (xhr.status === 401) {
          clearSession();
          window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
          reject(new ApiError(401, 'Your session has expired. Please sign in again.'));
          return;
        }
        let parsed: unknown = null;
        try {
          parsed = JSON.parse(xhr.responseText);
        } catch {
          /* fall through to the status-code message below */
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          const body = parsed as { results?: UploadResultRow[] } | UploadResultRow | null;
          const row =
            body && 'results' in (body as object) && Array.isArray((body as { results: UploadResultRow[] }).results)
              ? (body as { results: UploadResultRow[] }).results[0]
              : (body as UploadResultRow | null);
          resolve(
            row ?? { document_id: null, status: 'completed', message: 'Uploaded.', filename: file.name },
          );
          return;
        }
        // A rejection (bad type, oversize, too many pages, encrypted, corrupt) may arrive as a
        // 4xx with the reason in `detail`. Surface that text verbatim — it is what the clerk acts on.
        const detail = (parsed as { detail?: unknown } | null)?.detail;
        const message =
          typeof detail === 'string' && detail
            ? detail
            : `Upload failed (HTTP ${xhr.status}). The file was not accepted.`;
        resolve({ document_id: null, status: 'rejected', message, filename: file.name });
      };

      xhr.onerror = () =>
        resolve({
          document_id: null,
          status: 'rejected',
          message: 'Network error — the file did not reach the server. Nothing was stored.',
          filename: file.name,
        });
      xhr.onabort = () =>
        resolve({
          document_id: null,
          status: 'rejected',
          message: 'Upload cancelled before it finished.',
          filename: file.name,
        });

      signal?.addEventListener('abort', () => xhr.abort());
      xhr.send(form);
    });
  },

  // --------------------------------------------------------- patient intake

  getIntakeOptions: () => request<IntakeOptions>('/intake/options'),

  lookupMrNumber: (mrNumber: string) =>
    request<IntakeLookupResponse>(`/intake/lookup${qs({ mr_number: mrNumber })}`),

  /**
   * Submit the intake form and its documents in one request.
   *
   * Uses XHR rather than fetch for the same reason the batch uploader does: a clerk attaching a
   * multi-megabyte scan needs to see progress, and fetch cannot report upload progress.
   */
  submitIntake(
    values: IntakeFormValues,
    files: File[],
    onProgress?: (fraction: number) => void,
    signal?: AbortSignal,
  ): Promise<IntakeResponse> {
    return new Promise<IntakeResponse>((resolve, reject) => {
      const form = new FormData();
      for (const [key, value] of Object.entries(values)) form.append(key, value ?? '');
      for (const file of files) form.append('files', file, file.name);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_BASE}/intake`);
      const token = getToken();
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
      };

      xhr.onload = () => {
        if (xhr.status === 401) {
          clearSession();
          window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
          reject(new ApiError(401, 'Your session has expired. Please sign in again.'));
          return;
        }
        let parsed: unknown = null;
        try {
          parsed = JSON.parse(xhr.responseText);
        } catch {
          /* fall through to the status-code message below */
        }
        if (xhr.status >= 200 && xhr.status < 300 && parsed) {
          resolve(parsed as IntakeResponse);
          return;
        }
        // Validation failures (missing MR number, unreadable date, discharge before admission)
        // arrive as a 422 with the reason in `detail`. That text is written for the clerk, so it
        // is surfaced verbatim rather than replaced with a generic message.
        const detail = (parsed as { detail?: unknown } | null)?.detail;
        reject(
          new ApiError(
            xhr.status,
            typeof detail === 'string' && detail
              ? detail
              : `Could not save this record (HTTP ${xhr.status}).`,
          ),
        );
      };

      xhr.onerror = () =>
        reject(new ApiError(0, 'Network error — nothing was saved. Check the connection and try again.'));
      xhr.onabort = () => reject(new ApiError(0, 'Cancelled before the record was saved.'));

      signal?.addEventListener('abort', () => xhr.abort());
      xhr.send(form);
    });
  },

  // ------------------------------------------------- prescription analyzer

  /**
   * Upload one prescription image/PDF and wait for the finished reading in the same response.
   * Unlike uploadDocument, this is synchronous end to end — it can legitimately take 30-60s (OCR
   * then an LLM call), so the timeout is generous and there is no polling to do afterwards.
   */
  analyzePrescription(
    file: File,
    onProgress?: (fraction: number) => void,
    signal?: AbortSignal,
  ): Promise<PrescriptionAnalysisResponse> {
    return new Promise((resolve, reject) => {
      const form = new FormData();
      form.append('file', file, file.name);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_BASE}/prescriptions/analyze`);
      xhr.timeout = 180_000;
      const token = getToken();
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
      };

      xhr.onload = () => {
        if (xhr.status === 401) {
          clearSession();
          window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
          reject(new ApiError(401, 'Your session has expired. Please sign in again.'));
          return;
        }
        let parsed: unknown = null;
        try {
          parsed = JSON.parse(xhr.responseText);
        } catch {
          /* handled by the status check below */
        }
        if (xhr.status >= 200 && xhr.status < 300 && parsed) {
          resolve(parsed as PrescriptionAnalysisResponse);
          return;
        }
        const detail = (parsed as { detail?: unknown } | null)?.detail;
        reject(
          new ApiError(
            xhr.status,
            typeof detail === 'string' && detail ? detail : `Analysis failed (HTTP ${xhr.status}).`,
          ),
        );
      };
      xhr.onerror = () => reject(new ApiError(0, 'Network error — the file did not reach the server.'));
      xhr.onabort = () => reject(new ApiError(0, 'Cancelled.'));
      xhr.ontimeout = () => reject(new ApiError(0, 'The analysis took too long and timed out.'));

      signal?.addEventListener('abort', () => xhr.abort());
      xhr.send(form);
    });
  },

  listRecentPrescriptionAnalyses: () =>
    request<PrescriptionAnalysisSummary[]>('/prescriptions/recent'),

  getPrescriptionAnalysis: (documentId: string) =>
    request<PrescriptionAnalysisResponse>(`/prescriptions/${documentId}`),

  // ---------------------------------------------------------------- pages

  listPages: (params: URLSearchParams) => request<Paged<PageSummary>>(`/pages${qs(params)}`),
  getPage: (pageVersionId: string) => request<PageDetail>(`/pages/${pageVersionId}`),

  reviewPage: (
    pageVersionId: string,
    payload: { action: PageReviewAction; comment?: string; payload?: Record<string, unknown> },
  ) =>
    request<PageReviewResult>(`/pages/${pageVersionId}/review`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),

  reprocessPage: (pageVersionId: string, stages: Array<'quality' | 'handwriting' | 'diagnosis' | 'prescription'>) =>
    request<{ job_ids: string[] }>(`/pages/${pageVersionId}/reprocess${qs({ stages })}`, { method: 'POST' }),

  /**
   * Attach a rescan to an existing page.
   *
   * Returns only the identity of the new version — not a PageDetail. The caller has to refetch the
   * page if it wants the full record, because the replacement is analysed asynchronously and a
   * snapshot taken here would show the new version before any of its results exist.
   */
  replacePage: (pageVersionId: string, file: File) => {
    const form = new FormData();
    form.append('file', file, file.name);
    return request<PageReplaceResult>(`/pages/${pageVersionId}/replace`, { method: 'POST', body: form });
  },

  // ------------------------------------------------------------ diagnoses

  listDiagnoses: (params: URLSearchParams) =>
    request<Paged<DiagnosisExtraction & { page?: unknown }>>(`/diagnoses${qs(params)}`),
  getDiagnosis: (id: string) => request<DiagnosisDetail>(`/diagnoses/${id}`),
  reviewDiagnosis: (
    id: string,
    payload: {
      action: DiagnosisReviewAction;
      corrected_text?: string;
      corrected_qualifier?: Qualifier;
      comment?: string;
    },
  ) =>
    request<DiagnosisDetail>(`/diagnoses/${id}/review`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),

  // --------------------------------------------------------------- other

  getDashboard: (params: URLSearchParams) => request<DashboardResponse>(`/dashboard${qs(params)}`),

  /** Returns a plain array — /jobs is one of the few list endpoints with no Paged wrapper. */
  listJobs: (params?: {
    state?: string;
    kind?: string;
    document_id?: string;
    limit?: string;
    offset?: string;
  }) =>
    request<Job[]>(`/jobs${qs(params)}`),
  cancelJob: (id: string) => request<Job>(`/jobs/${id}/cancel`, { method: 'POST' }),

  getThresholds: () => request<ThresholdsResponse>('/settings/thresholds'),
  /**
   * Set retention periods. The backend takes the map directly — no `values` wrapper here, unlike
   * thresholds.
   */
  putRetention: (values: RetentionInfo) =>
    request<RetentionInfo>('/settings/retention', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(values),
    }),

  putThresholds: (thresholds: Record<string, number>) =>
    request<ThresholdsResponse>('/settings/thresholds', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ values: thresholds }),
    }),
  getCapabilities: () => request<CapabilitiesResponse | Record<string, Capability>>('/settings/capabilities'),

  listChecklists: () => request<Checklist[]>('/checklists'),
  createChecklist: (payload: ChecklistInput) =>
    request<Checklist>('/checklists', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  updateChecklist: (id: string, payload: ChecklistInput) =>
    request<Checklist>(`/checklists/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  deleteChecklist: (id: string) => request<void>(`/checklists/${id}`, { method: 'DELETE' }),
};

/** Report endpoints, kept separate because they are downloads rather than JSON. */
export const reports = {
  csv: (params: URLSearchParams) => downloadFile(`/reports/pages.csv${qs(params)}`, 'pages.csv'),
  xlsx: (params: URLSearchParams) => downloadFile(`/reports/pages.xlsx${qs(params)}`, 'pages.xlsx'),
  pdf: (params: URLSearchParams) => downloadFile(`/reports/pages.pdf${qs(params)}`, 'pages.pdf'),
  rescanChecklist: (params: URLSearchParams) =>
    downloadFile(`/reports/rescan-checklist.pdf${qs(params)}`, 'rescan-checklist.pdf'),
  flaggedZip: (params: URLSearchParams, annotated: boolean) => {
    const sp = new URLSearchParams(params);
    sp.set('annotated', String(annotated));
    return downloadFile(`/reports/flagged.zip${qs(sp)}`, 'flagged-pages.zip');
  },
};

// ------------------------------------------------------------ image paths

export const imagePath = {
  thumb: (pageVersionId: string) => `/pages/${pageVersionId}/thumb`,
  preview: (pageVersionId: string) => `/pages/${pageVersionId}/preview`,
  original: (pageVersionId: string) => `/pages/${pageVersionId}/image`,
  /** `show` selects which overlay families the server burns in. */
  annotated: (pageVersionId: string, show: string[]) =>
    `/pages/${pageVersionId}/annotated${show.length ? `?show=${show.join(',')}` : ''}`,
};
